import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, Zap, FileText, Workflow, Sparkles, Check, ArrowRight } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FileText className="w-8 h-8 text-violet-600" />,
      title: 'Smart Document Parsing',
      description: 'Upload Word documents and automatically extract fields in {{fieldname}} format. No manual setup required.'
    },
    {
      icon: <Workflow className="w-8 h-8 text-violet-600" />,
      title: 'Drag & Drop Form Builder',
      description: 'Build beautiful forms with intuitive drag-and-drop interface. Configure field types, defaults, and conditional logic.'
    },
    {
      icon: <Zap className="w-8 h-8 text-violet-600" />,
      title: 'Instant Mail Merge',
      description: 'Generate personalized documents in seconds. Replace placeholders with form data using smart mail-merge technology.'
    },
    {
      icon: <Sparkles className="w-8 h-8 text-violet-600" />,
      title: 'Template Management',
      description: 'Save and reuse templates across your organization. Run templates multiple times with different data sets.'
    }
  ];

  const plans = [
    {
      name: 'Free',
      price: 0,
      period: 'forever',
      description: 'Perfect for trying out TwentyDocs',
      features: [
        '10 documents per month',
        '10MB file size limit',
        'Basic templates',
        'Email support'
      ],
      cta: 'Get Started Free',
      popular: false
    },
    {
      name: 'Professional',
      price: 34,
      period: 'month',
      yearlyPrice: 346.8,
      description: 'For growing teams and businesses',
      features: [
        'Unlimited documents',
        '10MB file size limit',
        'Advanced templates',
        'Conditional logic',
        'Priority support'
      ],
      cta: 'Start Pro Trial',
      popular: true
    },
    {
      name: 'Business',
      price: 49,
      period: 'month',
      yearlyPrice: 499.8,
      description: 'For large organizations',
      features: [
        'Unlimited documents',
        '25MB file size limit',
        'All Pro features',
        'Custom branding',
        'API access',
        'Dedicated support'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div 
              className="flex items-center gap-2 sm:gap-3 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/ttoifebz_twenty-docs-logo-small.jpg"
                alt="TwentyDocs"
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg"
              />
              <div>
                <span className="text-lg sm:text-2xl font-bold gradient-text block leading-tight">TwentyDocs</span>
                <span className="text-[8px] sm:text-[9px] text-slate-500 block leading-tight -mt-0.5">
                  A Twenty Digital Product
                </span>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <button 
                onClick={() => navigate('/how-it-works')} 
                className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors"
              >
                How it works?
              </button>
              <button 
                onClick={() => navigate('/pricing')} 
                className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => navigate('/contact')} 
                className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors"
              >
                Contact
              </button>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/auth')}
                className="hidden sm:flex rounded-full font-medium text-sm sm:text-base"
                data-testid="nav-login-btn"
              >
                Log in
              </Button>
              <Button
                onClick={() => navigate('/auth')}
                className="h-9 px-4 sm:h-11 sm:px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-bold shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base"
                data-testid="nav-signup-btn"
              >
                <span className="hidden sm:inline">Get Started</span>
                <span className="sm:hidden">Start</span>
                <ChevronRight className="ml-1 sm:ml-2 w-3 h-3 sm:w-4 sm:h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 md:pt-40 md:pb-32 px-4 sm:px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-indigo-50 to-purple-50 opacity-60"></div>
        <div className="absolute top-20 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-violet-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white border border-violet-200 shadow-sm mb-6 sm:mb-8">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-violet-600" />
              <span className="text-xs sm:text-sm font-medium text-slate-700">Automate documents with magic</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.1] mb-4 sm:mb-6 px-4">
              Document Automation
              <span className="block gradient-text mt-2">Made Simple</span>
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg leading-relaxed text-slate-600 mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
              Transform your Word documents into dynamic templates. Extract fields, build forms, and generate personalized documents in seconds. No code required.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
              <Button
                onClick={() => navigate('/auth')}
                size="lg"
                className="w-full sm:w-auto h-12 sm:h-14 px-8 sm:px-10 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-base sm:text-lg font-bold shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                data-testid="hero-get-started-btn"
              >
                Get Started Free <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
              </Button>
              <Button
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="w-full sm:w-auto h-12 sm:h-14 px-8 sm:px-10 rounded-full border-2 border-slate-200 hover:border-violet-600 hover:text-violet-600 text-base sm:text-lg font-bold transition-all duration-300"
                data-testid="hero-view-pricing-btn"
              >
                View Pricing
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 md:py-32 px-4 sm:px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3 sm:mb-4 px-4">Powerful Features</h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              Everything you need to automate your document workflows
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
            {/* Smart Document Parsing - First Feature with Image */}
            <div
              onClick={() => navigate('/features/smart-document-parsing')}
              className="group p-6 sm:p-8 rounded-2xl sm:rounded-3xl bg-white border border-slate-100 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-violet-200/50 transition-all duration-300 cursor-pointer hover:scale-105"
              data-testid="feature-card-0"
            >
              <div className="mb-4 sm:mb-6 rounded-lg sm:rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-violet-50 p-3 sm:p-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/cnigja5d_smart-document-parsing.jpg"
                  alt="Smart Document Parsing"
                  className="w-full h-auto rounded-lg"
                />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">Smart Document Parsing</h3>
              <p className="text-sm sm:text-base leading-relaxed text-slate-600 mb-2 sm:mb-3">
                Upload Word documents and automatically extract fields in {'{fieldname}'} format. No manual setup required.
              </p>
              <span className="text-xs sm:text-sm text-violet-600 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn more <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </span>
            </div>
            {/* Remaining Features */}
            {features.slice(1).map((feature, index) => {
              // Use custom images for specific features
              const isDragDropFeature = index === 0; // Drag & Drop Form Builder
              const isMailMergeFeature = index === 1; // Instant Mail Merge
              const isTemplateManagement = index === 2; // Template Management
              
              // Determine if clickable and route
              const isClickable = isDragDropFeature || isMailMergeFeature || isTemplateManagement;
              const clickRoute = isDragDropFeature ? '/features/drag-drop-form-builder' 
                                : isMailMergeFeature ? '/features/instant-mail-merge'
                                : isTemplateManagement ? '/features/template-management'
                                : null;
              
              return (
                <div
                  key={index + 1}
                  onClick={() => isClickable && navigate(clickRoute)}
                  className={`group p-6 sm:p-8 rounded-2xl sm:rounded-3xl bg-white border border-slate-100 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-violet-200/50 transition-all duration-300 ${
                    isClickable ? 'cursor-pointer hover:scale-105' : ''
                  }`}
                  data-testid={`feature-card-${index + 1}`}
                >
                  {isDragDropFeature ? (
                    <div className="mb-4 sm:mb-6 rounded-lg sm:rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-violet-50 p-3 sm:p-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/ea2crrkj_drag-drop-form.jpg"
                        alt="Drag & Drop Form Builder"
                        className="w-full h-auto rounded-lg"
                      />
                    </div>
                  ) : isMailMergeFeature ? (
                    <div className="mb-4 sm:mb-6 rounded-lg sm:rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-violet-50 p-3 sm:p-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/3hj7nols_ms-word-style-mail-merge.jpg"
                        alt="Instant Mail Merge"
                        className="w-full h-auto rounded-lg"
                      />
                    </div>
                  ) : isTemplateManagement ? (
                    <div className="mb-4 sm:mb-6 rounded-lg sm:rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-violet-50 p-3 sm:p-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/a0qqdf54_templates.jpg"
                        alt="Template Management"
                        className="w-full h-auto rounded-lg"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-violet-50 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>
                  )}
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">{feature.title}</h3>
                  <p className="text-sm sm:text-base leading-relaxed text-slate-600 mb-2 sm:mb-3">{feature.description}</p>
                  {isClickable && (
                    <span className="text-xs sm:text-sm text-violet-600 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                      Learn more <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-20 md:py-32 px-4 sm:px-6 md:px-12 bg-gradient-to-br from-slate-50 to-violet-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3 sm:mb-4 px-4">Simple, Transparent Pricing</h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              Choose the plan that fits your needs. Save 15% with annual billing.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative p-6 sm:p-8 rounded-2xl sm:rounded-3xl bg-white transition-all duration-300 ${
                  plan.popular
                    ? 'border-2 border-violet-500 shadow-2xl shadow-violet-200/50 md:scale-105'
                    : 'border-2 border-slate-100 hover:border-violet-500 shadow-xl'
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                    <span className="inline-block px-3 sm:px-4 py-1 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs sm:text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-xl sm:text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-3">
                    <span className="text-4xl sm:text-5xl font-extrabold">${plan.price}</span>
                    <span className="text-slate-600 ml-2 text-sm sm:text-base">/ {plan.period}</span>
                  </div>
                  {plan.yearlyPrice && (
                    <p className="text-xs sm:text-sm text-slate-500">
                      or ${plan.yearlyPrice}/year (save 15%)
                    </p>
                  )}
                  <p className="text-slate-600 mt-3 text-sm sm:text-base">{plan.description}</p>
                </div>
                
                <ul className="space-y-3 mb-6 sm:mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm sm:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button
                  onClick={() => navigate('/pricing')}
                  className={`w-full h-11 sm:h-12 rounded-full font-bold transition-all duration-300 text-sm sm:text-base ${
                    plan.popular
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl'
                      : 'bg-white text-slate-900 border-2 border-slate-200 hover:border-violet-600 hover:text-violet-600'
                  }`}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 md:py-32 px-4 sm:px-6 md:px-12 bg-gradient-to-br from-violet-600 via-indigo-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
            Ready to Automate Your Documents?
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-8 sm:mb-10 text-violet-100 px-4">
            Join thousands of teams already using TwentyDocs to streamline their workflows.
          </p>
          <Button
            onClick={() => navigate('/auth')}
            size="lg"
            className="w-full sm:w-auto h-12 sm:h-14 px-8 sm:px-10 rounded-full bg-white text-violet-600 hover:bg-violet-50 text-base sm:text-lg font-bold shadow-2xl hover:scale-105 transition-all duration-300 mx-4"
            data-testid="cta-get-started-btn"
          >
            Start Free Today <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-12 px-4 sm:px-6 md:px-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="text-lg sm:text-xl font-bold text-white">TwentyDocs</span>
          </div>
          <p className="text-xs sm:text-sm">© 2025 TwentyDocs. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;